import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, InputAdornment,
  Container, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  FormControl, InputLabel, Select, Grid, Tabs, Tab, Card, CardContent,
  useMediaQuery, useTheme
} from '@mui/material';
import { Search, FilterList, Refresh, History, Edit } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { getRawMaterialsAction, createRawMaterialsAction, editRawMaterialAction, clearEditRawMaterialAction, deleteRawMaterialAction, clearDeleteRawMaterialAction, postRecaluculateAction, clearPostRecaluculateAction, getRawMaterialHistoryAction } from '../../store/actions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { quantityTypes } from '../../utils/QuantityType';

const getStatusColor = (currentStock, reorderLevel, requiredQty, stockInTtransit, reservedQty) => {
  if (stockInTtransit < requiredQty) {
    return { backgroundColor: '#fce8e6', color: '#d93025', minWidth: '80px' };
  } else if (stockInTtransit > requiredQty) {
    return { backgroundColor: '#fce8e6', color: '#FFA500', minWidth: '80px' };
  } else if ((currentStock - reservedQty) < reorderLevel) {
    if (stockInTtransit > reorderLevel) {
      return { backgroundColor: '#fce8e6', color: '#FFA500', minWidth: '80px' };
    } else {
      return { backgroundColor: '#fce8e6', color: '#343434', minWidth: '80px' };
    }
  } else {
    return { backgroundColor: '#90EE90', color: '#FFFFE0', minWidth: '80px' };
  }
};


const getAlertStatus = (currentStock, reorderLevel, requiredQty, stockInTtransit, reservedQty) => {
  if (stockInTtransit < requiredQty) {
    return 'urgent';
  } else if (stockInTtransit > requiredQty) {
    return 'InTransit';
  } else if ((currentStock - reservedQty) < reorderLevel) {
    if (stockInTtransit > reorderLevel) {
      return 'InTransit';
    } else {
      return 'Reorder';
    }
  } else {
    return 'ok';
  }
};

// TabPanel component for the history dialog
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`history-tabpanel-${index}`}
      aria-labelledby={`history-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Material Card component for mobile view
const MaterialCard = ({ material, onEdit, onHistory }) => {
  const requiredQty = material.reserved_qty > material.stock_qty
    ? material.reserved_qty - material.stock_qty
    : 0;
  const alertStatus = getAlertStatus(material.stock_qty, material.reorder_level, requiredQty, material.stock_in_transit, material.reserved_qty);

  // Map alert status to more user-friendly display text
  const getAlertStatusText = (status) => {
    switch(status) {
      case 'urgent': return 'Urgent Stock Required';
      case 'InTransit': return 'Stock In Transit';
      case 'Reorder': return 'Reorder Required';
      case 'ok': return 'Stock Level OK';
      default: return status;
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #E6EFF5',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {/* Alert status banner at the top of the card */}
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '6px 12px',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          ...getStatusColor(material.stock_qty, material.reorder_level, requiredQty, material.stock_in_transit, material.reserved_qty),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontWeight: 'bold',
          fontSize: '0.85rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        {getAlertStatusText(alertStatus)}
      </Box>

      <CardContent sx={{ pt: 5 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {material.name}
        </Typography>

        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Current Stock:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: material.stock_qty < material.reorder_level ? '#d93025' : '#1e8e3e',
                fontWeight: 'medium'
              }}
            >
              {material.stock_qty} {material.qty_type}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Reserved for Orders:
            </Typography>
            <Typography variant="body1">
              {material.reserved_qty}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Stock in Progress:
            </Typography>
            <Typography variant="body1">
              {material.stock_in_progress || 0}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Required Stock:
            </Typography>
            <Typography variant="body1" sx={{
              fontWeight: requiredQty > 0 ? 'bold' : 'normal',
              color: requiredQty > 0 ? '#d93025' : 'inherit'
            }}>
              {requiredQty}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Stock in Transit:
            </Typography>
            <Typography variant="body1" sx={{
              fontWeight: material.stock_in_transit > 0 ? 'medium' : 'normal'
            }}>
              {material.stock_in_transit || 0}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Reorder Level:
            </Typography>
            <Typography variant="body1">
              {material.reorder_level}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<Edit />}
            onClick={() => onEdit(material)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#1814F3',
              color: '#fff',
              borderRadius: '10px',
              fontFamily: 'Inter',
            }}
          >
            Update
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<History />}
            onClick={() => onHistory(material)}
            sx={{
              textTransform: 'none',
              backgroundColor: 'red',
              color: '#fff',
              borderRadius: '10px',
              fontFamily: 'Inter',
            }}
          >
            History
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};


const StockManagement = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [alertFilter, setAlertFilter] = useState('all');
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState(null);
  const [historyTabValue, setHistoryTabValue] = useState(1);

  const { successData } = useSelector((state) => state.getRawMaterialsReducer);
  const { successData: createSuccessData } = useSelector((state) => state.createRawMaterialReducer);
  const { successData: editSuccessData } = useSelector((state) => state.editRawMaterialReducer);
  const { successData: deleteSuccessData } = useSelector((state) => state.deleteRawMaterialReducer);
  const { successData: recalculateSuccessData, isLoading: recalculateLoading } = useSelector((state) => state.recaluculateReducer || {});
  const { successData: rawMaterialHistoryData } = useSelector((state) => state.RawMaterialHistoryReduce || {});
  const rawMaterials = successData?.data || [];

  // Sort materials alphabetically by name
  const sortedMaterials = [...rawMaterials].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  // Updated filtering logic to include both search term and alert status
  const filteredMaterials = sortedMaterials.filter(material => {
    const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase());

    if (alertFilter === 'all') {
      return matchesSearch;
    }

    const requiredQty = material.reserved_qty > material.stock_qty
      ? material.reserved_qty - material.stock_qty
      : 0;

    const alertStatus = getAlertStatus(
      material.stock_qty,
      material.reorder_level,
      requiredQty,
      material.stock_in_transit,
      material.reserved_qty
    );

    return matchesSearch && alertStatus === alertFilter;
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      stock_qty: '',
      qty_type: '',
      reorder_level: '',
      stock_in_transit: '',
      delivery_time: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Raw Material Name is required'),
      stock_qty: Yup.number().required('Stock Quantity is required').min(0, 'Stock Quantity Cannot be negative'),
      qty_type: Yup.string().required('Quantity Type is required'),
      reorder_level: Yup.number().required('Reorder Level is required').min(0, 'Reorder Level Cannot be negative'),
      stock_in_transit: Yup.number().required('Stock in Transit is required').min(0, 'Cannot be negative'),
      delivery_time: Yup.number().required('Delivery Time is required').positive('Must be a positive number'),
    }),
    onSubmit: (values) => {
      if (isEditMode && currentItemId) {
        console.log('currentItemId', currentItemId);
        dispatch(editRawMaterialAction({
          endPoint: `admin/edit-raw-material/${currentItemId}`,
          ...values,
        }));
      } else {
        dispatch(createRawMaterialsAction(values));
      }
      handleCloseDialog();
    },
  });

  useEffect(() => {
    dispatch(getRawMaterialsAction());
  }, [dispatch]);

  useEffect(() => {
    if (createSuccessData) {
      toast.success(createSuccessData.message);
      dispatch(getRawMaterialsAction());
    }
  }, [createSuccessData, dispatch]);

  // Handle recalculate action
  const handleRecalculate = () => {
    setRecalculateDialogOpen(true);
  };

  const handleConfirmRecalculate = () => {
    setIsRecalculating(true);
    setRecalculateDialogOpen(false);
    dispatch(postRecaluculateAction());
  };

  const handleCancelRecalculate = () => {
    setRecalculateDialogOpen(false);
  };

  // Add effect for recalculate success
  useEffect(() => {
    if (recalculateSuccessData) {
      toast.success(recalculateSuccessData.message || 'Stock recalculated successfully');
      dispatch(getRawMaterialsAction());
      setIsRecalculating(false);
      dispatch(clearPostRecaluculateAction)
    }
  }, [recalculateSuccessData, dispatch]);

  const handleEditClick = (material) => {
    setIsEditMode(true);
    setCurrentItemId(material.id);
    dispatch(getRawMaterialsAction({
      id: material.id
    }));
    formik.setValues({
      name: material.name,
      stock_qty: material.stock_qty,
      qty_type: material.qty_type,
      reorder_level: material.reorder_level,
      stock_in_transit: material.stock_in_transit || 0,
      delivery_time: material.delivery_time,
    });
    setIsOpen(true);
  };

  const handleHistoryClick = (material) => {
    console.log("material", material.id);
    dispatch(getRawMaterialHistoryAction({
      id: material.id,
      endPoint: `admin/raw-material/history/${material.id}`
    }));
    setCurrentMaterial(material);
    setHistoryDialogOpen(true);
    setHistoryTabValue(0);
  };

  const handleHistoryTabChange = (event, newValue) => {
    setHistoryTabValue(newValue);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setCurrentMaterial(null);
    setHistoryTabValue(0);
  };

  const handleOpenDialog = () => {
    setIsEditMode(false);
    setCurrentItemId(null);
    formik.resetForm();
    setIsOpen(true);
  };

  const handleCloseDialog = () => {
    setIsOpen(false);
    setIsEditMode(false);
    setCurrentItemId(null);
    formik.resetForm();
  };

  const handleAlertFilterChange = (event) => {
    setAlertFilter(event.target.value);
  };

  // Add effect for edit success
  useEffect(() => {
    if (editSuccessData) {
      toast.success(editSuccessData.message || 'Raw material updated successfully');
      dispatch(getRawMaterialsAction());
    }
    return () => {
      dispatch(clearEditRawMaterialAction());
    };
  }, [editSuccessData, dispatch]);

  useEffect(() => {
    if (deleteSuccessData) {
      toast.success(deleteSuccessData.message || 'Raw material deleted successfully');
      dispatch(getRawMaterialsAction());
    }
    return () => {
      dispatch(clearDeleteRawMaterialAction());
    };
  }, [deleteSuccessData, dispatch]);

  return (
    <Container maxWidth="xl" sx={{ 
      py: 4, 
      width: { xs: '95%', sm: '80%' }, 
      ml: { xs: 'auto', sm: '10%', md: '20%' }, 
      mr: { xs: 'auto' },
      mt: { xs: '13%', sm: '10%', md: '7%' } 
    }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 2,
            gap: isMobile ? 2 : 0
          }}>
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ 
              fontFamily: 'Poppins',
              fontSize: isMobile ? '1.5rem' : '2rem'
            }}>
              Material Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRecalculate}
              disabled={isRecalculating || recalculateLoading}
              sx={{
                borderColor: '#1814F3',
                color: '#1814F3',
                borderRadius: '10px',
                fontFamily: 'Inter',
                '&:hover': {
                  borderColor: '#0e0c9e',
                  backgroundColor: 'rgba(24, 20, 243, 0.04)'
                }
              }}
            >
              {isRecalculating ? 'Recalculating...' : 'Recalculate'}
            </Button>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 2
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center', 
              gap: 2,
              width: isMobile ? '100%' : 'auto'
            }}>
              <TextField
                placeholder="Search"
                variant="outlined"
                size="small"
                fullWidth={isMobile}
                sx={{ width: isMobile ? '100%' : 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FormControl 
                variant="outlined" 
                size="small" 
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? '100%' : 180 }}
              >
                <InputLabel id="alert-filter-label">Filter by Alert Status</InputLabel>
                <Select
                  labelId="alert-filter-label"
                  id="alert-filter"
                  value={alertFilter}
                  onChange={handleAlertFilterChange}
                  label="Filter by Alert Status"
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterList fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Alerts</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="InTransit">In Transit</MenuItem>
                  <MenuItem value="Reorder">Reorder</MenuItem>
                  <MenuItem value="ok">OK</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
              <Button
                variant="contained"
                onClick={handleOpenDialog}
                fullWidth={isMobile}
                sx={{
                  backgroundColor: '#1814F3',
                  color: '#fff',
                  borderRadius: '10px',
                  fontFamily: 'Inter',
                }}
              >
                Create New Raw Materials
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Conditional rendering based on screen size */}
        {isMobile ? (
          // Mobile view with cards
          <Box sx={{ mt: 3 }}>
            {filteredMaterials.length > 0 ? (
              filteredMaterials.map((material) => (
                <MaterialCard 
                  key={material.id} 
                  material={material} 
                  onEdit={handleEditClick}
                  onHistory={handleHistoryClick}
                />
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No materials found matching your criteria
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Desktop view with table
          <TableContainer component={Paper} sx={{
            boxShadow: 'none',
            '& .MuiTable-root': {
              borderCollapse: 'collapse'
            },
            mt: '2%'
          }}>
            <Table sx={{
              minWidth: 650,
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #E6EFF5',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none'
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                borderBottom: '1px solid #E6EFF5'
              }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Stock Name</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Current Stock</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Reserved for Orders</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Stock in Progress</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Required Stock</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Stock in Transit</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Alerts</TableCell>
                  <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMaterials.map((material) => {
                  const requiredQty = material.reserved_qty > material.stock_qty
                    ? material.reserved_qty - material.stock_qty
                    : 0;
                  const alertStatus = getAlertStatus(material.stock_qty, material.reorder_level, requiredQty, material.stock_in_transit, material.reserved_qty);
                  return (
                    <TableRow key={material.id}>
                      <TableCell sx={{ color: '#292D32', fontFamily: 'Poppins' }}>{material.name}</TableCell>
                      <TableCell sx={{
                        color: material.stock_qty < material.reorder_level ? '#d93025' : '#1e8e3e',
                        fontWeight: 'medium'
                      }}>
                        {material.stock_qty} {material.qty_type}
                      </TableCell>
                                            <TableCell>{material.reserved_qty}</TableCell>
                      <TableCell>{material.stock_in_progress || 0}</TableCell>
                      <TableCell>{requiredQty}</TableCell>
                      <TableCell>{material.stock_in_transit || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={alertStatus}
                          sx={{
                            ...getStatusColor(material.stock_qty, material.reorder_level, requiredQty, material.stock_in_transit, material.reserved_qty),
                            minWidth: '80px',
                            justifyContent: 'center'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            sx={{
                              textTransform: 'none',
                              backgroundColor: '#1814F3',
                              color: '#fff',
                              borderRadius: '10px',
                              fontFamily: 'Inter',
                            }}
                            onClick={() => handleEditClick(material)}
                          >
                            Update
                          </Button>
                          <Button
                            sx={{
                              textTransform: 'none',
                              backgroundColor: 'red',
                              color: '#fff',
                              borderRadius: '10px',
                              fontFamily: 'Inter',
                            }}
                            onClick={() => handleHistoryClick(material)}
                            startIcon={<History />}
                          >
                            History
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredMaterials.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No materials found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Create/Edit Dialog - Same for both mobile and desktop */}
      <Dialog 
        open={isOpen} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: { xs: 1, sm: 2 }
          }
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ 
            fontFamily: 'Poppins', 
            fontWeight: 'bold',
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }}>
            {isEditMode ? 'Edit Raw Material' : 'Create New Raw Material'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2, 
              mt: 1
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
                <TextField
                  label="Raw Material Name"
                  fullWidth
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <TextField
                  label="Stock Quantity"
                  fullWidth
                  type="number"
                  name="stock_qty"
                  value={formik.values.stock_qty}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.stock_qty && Boolean(formik.errors.stock_qty)}
                  helperText={formik.touched.stock_qty && formik.errors.stock_qty}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
                <TextField
                  select
                  label="Quantity Type"
                  fullWidth
                  name="qty_type"
                  value={formik.values.qty_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.qty_type && Boolean(formik.errors.qty_type)}
                  helperText={formik.touched.qty_type && formik.errors.qty_type}
                >
                  {quantityTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{
                      fontSize: '14px',
                      minHeight: '30px',
                    }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Reorder Level"
                  fullWidth
                  type="number"
                  name="reorder_level"
                  value={formik.values.reorder_level}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.reorder_level && Boolean(formik.errors.reorder_level)}
                  helperText={formik.touched.reorder_level && formik.errors.reorder_level}
                />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
                <TextField
                  label="Stock in Transit"
                  fullWidth
                  type="number"
                  name="stock_in_transit"
                  value={formik.values.stock_in_transit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.stock_in_transit && Boolean(formik.errors.stock_in_transit)}
                  helperText={formik.touched.stock_in_transit && formik.errors.stock_in_transit}
                />
                <TextField
                  label="Delivery Time (Days)"
                  fullWidth
                  type="number"
                  name="delivery_time"
                  value={formik.values.delivery_time}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.delivery_time && Boolean(formik.errors.delivery_time)}
                  helperText={formik.touched.delivery_time && formik.errors.delivery_time}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCloseDialog} 
              variant="outlined"
              sx={{
                borderColor: '#ccc',
                color: '#666',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                backgroundColor: '#1814F3',
                color: '#fff',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#0e0c9e',
                }
              }}
            >
              {isEditMode ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Recalculate Confirmation Dialog */}
      <Dialog 
        open={recalculateDialogOpen} 
        onClose={handleCancelRecalculate}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: { xs: 1, sm: 1 }
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 'medium' }}>
          Confirm Recalculation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to recalculate the stock? This action will update all stock calculations.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancelRecalculate} 
            variant="outlined"
            sx={{
              borderColor: '#ccc',
              color: '#666',
              borderRadius: '8px',
            }}
          >
            No
          </Button>
          <Button 
            onClick={handleConfirmRecalculate} 
            variant="contained"
            sx={{
              backgroundColor: '#1814F3',
              color: '#fff',
              borderRadius: '8px',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog with Tabs - Responsive for both mobile and desktop */}
      <Dialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            width: { xs: '95%', sm: '80%', md: '100%' }
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Stock History: {currentMaterial?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Tabs for switching between transaction and consumption history */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={historyTabValue}
              onChange={handleHistoryTabChange}
              aria-label="history tabs"
              variant={isMobile ? "fullWidth" : "standard"}
            >
              <Tab label="Consumption History" id="history-tab-1" aria-controls="history-tabpanel-1" />
              <Tab label="Transaction History" id="history-tab-0" aria-controls="history-tabpanel-0" />
            </Tabs>
          </Box>

          {/* Consumption History Tab Panel */}
          <TabPanel value={historyTabValue} index={1}>
            {isMobile ? (
              // Mobile view with cards for consumption history
              <Box>
                {rawMaterialHistoryData?.data?.consumptions?.length > 0 ? (
                  rawMaterialHistoryData.data.consumptions.map((consumption) => {
                    const dateTime = new Date(consumption.deducted_at);
                    const date = dateTime.toLocaleDateString();
                    const time = dateTime.toLocaleTimeString();
                    
                    return (
                      <Card key={consumption.id} sx={{ mb: 2, borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Date:</Typography>
                              <Typography variant="body1">{date}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Time:</Typography>
                              <Typography variant="body1">{time}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                              <Typography variant="body1">{consumption.order_id}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Product:</Typography>
                              <Typography variant="body1">{consumption?.Order?.Machine.name || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">Quantity Used:</Typography>
                              <Typography variant="body1" fontWeight="medium">{consumption.quantity_used}</Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No consumption records found
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              // Desktop view with table for consumption history
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantity Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rawMaterialHistoryData?.data?.consumptions?.map((consumption) => {
                      // Format date and time
                      const dateTime = new Date(consumption.deducted_at);
                      const date = dateTime.toLocaleDateString();
                      const time = dateTime.toLocaleTimeString();

                      return (
                        <TableRow key={consumption.id}>
                          <TableCell>{date}</TableCell>
                          <TableCell>{time}</TableCell>
                          <TableCell>{consumption.order_id}</TableCell>
                          <TableCell>{consumption?.Order?.Machine.name || 'N/A'}</TableCell>
                          <TableCell>{consumption.quantity_used}</TableCell>
                        </TableRow>
                      );
                    })}
                    {(!rawMaterialHistoryData?.data?.consumptions || rawMaterialHistoryData.data.consumptions.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No consumption records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Transaction History Tab Panel */}
          <TabPanel value={historyTabValue} index={0}>
            {isMobile ? (
              // Mobile view with cards for transaction history
              <Box>
                {rawMaterialHistoryData?.data?.transactions?.length > 0 ? (
                  rawMaterialHistoryData.data.transactions.map((record) => {
                    const dateTime = new Date(record.created_at);
                    const date = dateTime.toLocaleDateString();
                    const time = dateTime.toLocaleTimeString();
                    
                    return (
                      <Card key={record.id} sx={{ mb: 2, borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Date:</Typography>
                              <Typography variant="body1">{date}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Time:</Typography>
                              <Typography variant="body1">{time}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                              <Typography variant="body1" fontWeight="medium">{record.quantity}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Type:</Typography>
                              <Typography variant="body1">{record.transaction_type}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">User:</Typography>
                              <Typography variant="body1">{record.User?.full_name || 'Unknown'}</Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No transaction records found
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              // Desktop view with table for transaction history
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Transfer Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rawMaterialHistoryData?.data?.transactions?.map((record) => {
                      // Format date and time
                      const dateTime = new Date(record.created_at);
                      const date = dateTime.toLocaleDateString();
                      const time = dateTime.toLocaleTimeString();

                      return (
                        <TableRow key={record.id}>
                          <TableCell>{date}</TableCell>
                          <TableCell>{time}</TableCell>
                          <TableCell>{record.quantity}</TableCell>
                          <TableCell>{record.transaction_type}</TableCell>
                          <TableCell>{record.User?.full_name || 'Unknown'}</TableCell>
                        </TableRow>
                      );
                    })}
                    {(!rawMaterialHistoryData?.data?.transactions || rawMaterialHistoryData.data.transactions.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No transaction records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseHistoryDialog} 
            variant="contained"
            sx={{
              backgroundColor: '#1814F3',
              color: '#fff',
              borderRadius: '8px',
              mb: 1,
              mr: 2
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StockManagement;

